import * as React from 'react';

type UseKeyboardNavigationOptions = {
  /** HTML Selector to target */
  selector?: string;
};

type UseKeyboardNavigationReturn = {
  /** Keyboard event handler for the container */
  handleKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
};

const isTabDisabled = (tab: HTMLElement): boolean => {
  return (
    tab.hasAttribute('disabled') || tab.getAttribute('aria-disabled') === 'true'
  );
};

export const useKeyboardNavigation = ({
  selector = '[role="tab"]',
}: UseKeyboardNavigationOptions = {}): UseKeyboardNavigationReturn => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    const currentTarget = e.currentTarget;
    const allTabs = Array.from(
      currentTarget.querySelectorAll<HTMLElement>(selector)
    );
    const selectableTabs = allTabs.filter((tab) => !isTabDisabled(tab));

    if (selectableTabs.length === 0) {
      return;
    }

    const currentIndex = selectableTabs.findIndex((tab) =>
      tab.contains(document.activeElement)
    );

    let targetIndex: number | null = null;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        targetIndex =
          currentIndex > 0 ? currentIndex - 1 : selectableTabs.length - 1;
        break;

      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        targetIndex =
          currentIndex < selectableTabs.length - 1 ? currentIndex + 1 : 0;
        break;

      case 'Home':
        e.preventDefault();
        e.stopPropagation();
        targetIndex = 0;
        break;

      case 'End':
        e.preventDefault();
        e.stopPropagation();
        targetIndex = selectableTabs.length - 1;
        break;

      default:
        return;
    }

    if (targetIndex !== null && selectableTabs[targetIndex]) {
      selectableTabs[targetIndex].focus();
    }
  };

  return {
    handleKeyDown,
  };
};
